import type { StatusConfig } from '../components/ui/StatusBadge';

// ============================================
// TYPES
// ============================================

export type StatusColorMap = Record<string, StatusConfig>;

// ============================================
// QUALITY RATING
// ============================================

export const qualityRatingColors: StatusColorMap = {
  high: { bg: 'bg-success/10', text: 'text-success', label: 'Alta' },
  green: { bg: 'bg-success/10', text: 'text-success', label: 'Alta' },
  medium: { bg: 'bg-warning/10', text: 'text-warning', label: 'Media' },
  yellow: { bg: 'bg-warning/10', text: 'text-warning', label: 'Media' },
  low: { bg: 'bg-danger/10', text: 'text-danger', label: 'Baixa' },
  red: { bg: 'bg-danger/10', text: 'text-danger', label: 'Baixa' },
  na: { bg: 'bg-bg-3', text: 'text-text-3', label: 'Pendente' },
  'n/a': { bg: 'bg-bg-3', text: 'text-text-3', label: 'Pendente' },
  default: { bg: 'bg-bg-3', text: 'text-text-3', label: 'N/A' },
};

export function getQualityRatingColor(rating?: string): string {
  const key = rating?.toLowerCase() || 'default';
  const config = qualityRatingColors[key] || qualityRatingColors['default'];
  return `${config.bg} ${config.text}`;
}

export function getQualityRatingLabel(rating?: string): string {
  const key = rating?.toLowerCase() || 'default';
  const config = qualityRatingColors[key] || qualityRatingColors['default'];
  return config.label || rating || 'N/A';
}

// ============================================
// ACCOUNT STATUS
// ============================================

export const accountStatusColors: StatusColorMap = {
  ready: { bg: 'bg-success/10', text: 'text-success', label: 'Pronta' },
  active: { bg: 'bg-success/10', text: 'text-success', label: 'Ativa' },
  connected: { bg: 'bg-success/10', text: 'text-success', label: 'Conectada' },
  pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pendente' },
  flagged: { bg: 'bg-warning/10', text: 'text-warning', label: 'Sinalizada' },
  error: { bg: 'bg-danger/10', text: 'text-danger', label: 'Erro' },
  failed: { bg: 'bg-danger/10', text: 'text-danger', label: 'Falhou' },
  banned: { bg: 'bg-danger/10', text: 'text-danger', label: 'Banida' },
  restricted: { bg: 'bg-danger/10', text: 'text-danger', label: 'Restrita' },
  inactive: { bg: 'bg-bg-3', text: 'text-text-3', label: 'Inativa' },
  default: { bg: 'bg-bg-3', text: 'text-text-3', label: 'N/A' },
};

export function getAccountStatusColor(status?: string): string {
  const key = status?.toLowerCase() || 'default';
  const config = accountStatusColors[key] || accountStatusColors['default'];
  return `${config.bg} ${config.text}`;
}

export function getAccountStatusLabel(status?: string): string {
  const key = status?.toLowerCase() || 'default';
  const config = accountStatusColors[key] || accountStatusColors['default'];
  return config.label || status || 'N/A';
}

// ============================================
// CAMPAIGN STATUS
// ============================================

export const campaignStatusColors: StatusColorMap = {
  draft: { bg: 'bg-bg-3', text: 'text-text-2', label: 'Rascunho' },
  processing: { bg: 'bg-info/10', text: 'text-info', label: 'Processando' },
  queued: { bg: 'bg-warning/10', text: 'text-warning', label: 'Na Fila' },
  scheduled: { bg: 'bg-accent-2/10', text: 'text-accent-2', label: 'Agendada' },
  running: { bg: 'bg-info/10', text: 'text-info', label: 'Em Execucao' },
  active: { bg: 'bg-success/10', text: 'text-success', label: 'Ativa' },
  paused: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pausada' },
  completed: { bg: 'bg-success/10', text: 'text-success', label: 'Concluida' },
  failed: { bg: 'bg-danger/10', text: 'text-danger', label: 'Falhou' },
  cancelled: { bg: 'bg-bg-3', text: 'text-text-3', label: 'Cancelada' },
  default: { bg: 'bg-bg-3', text: 'text-text-2', label: 'Desconhecido' },
};

// ============================================
// TEMPLATE STATUS
// ============================================

export const templateStatusColors: StatusColorMap = {
  pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pendente' },
  approved: { bg: 'bg-success/10', text: 'text-success', label: 'Aprovado' },
  rejected: { bg: 'bg-danger/10', text: 'text-danger', label: 'Rejeitado' },
  active_quality_pending: { bg: 'bg-success/10', text: 'text-success', label: 'Ativo - Qualidade Pendente' },
  active_high_quality: { bg: 'bg-success/10', text: 'text-success', label: 'Ativo - Alta Qualidade' },
  active_medium_quality: { bg: 'bg-warning/10', text: 'text-warning', label: 'Ativo - Media Qualidade' },
  active_low_quality: { bg: 'bg-danger/10', text: 'text-danger', label: 'Ativo - Baixa Qualidade' },
  paused: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pausado' },
  disabled: { bg: 'bg-danger/10', text: 'text-danger', label: 'Desabilitado' },
  default: { bg: 'bg-bg-3', text: 'text-text-2', label: 'Desconhecido' },
};

export function getTemplateStatusColor(status: string): string {
  const key = status.toLowerCase();
  // Handle ACTIVE_* variants
  if (key.startsWith('active')) {
    const config = templateStatusColors[key] || templateStatusColors['approved'];
    return `${config.bg} ${config.text}`;
  }
  const config = templateStatusColors[key] || templateStatusColors['default'];
  return `${config.bg} ${config.text}`;
}

export function getTemplateStatusLabel(status: string): string {
  const key = status.toLowerCase();
  const config = templateStatusColors[key] || templateStatusColors['default'];
  return config.label || status;
}

// ============================================
// INVOICE STATUS
// ============================================

export const invoiceStatusColors: StatusColorMap = {
  draft: { bg: 'bg-bg-3', text: 'text-text-2', label: 'Rascunho' },
  pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Aguardando' },
  billed: { bg: 'bg-info/10', text: 'text-info', label: 'Faturado' },
  paid: { bg: 'bg-success/10', text: 'text-success', label: 'Pago' },
  overdue: { bg: 'bg-danger/10', text: 'text-danger', label: 'Vencido' },
  cancelled: { bg: 'bg-bg-3', text: 'text-text-3', label: 'Cancelado' },
  default: { bg: 'bg-bg-3', text: 'text-text-2', label: 'Desconhecido' },
};

// ============================================
// TENANT STATUS
// ============================================

export const tenantStatusColors: StatusColorMap = {
  active: { bg: 'bg-success/10', text: 'text-success', label: 'Ativo' },
  trial: { bg: 'bg-info/10', text: 'text-info', label: 'Trial' },
  inactive: { bg: 'bg-bg-3', text: 'text-text-3', label: 'Inativo' },
  suspended: { bg: 'bg-danger/10', text: 'text-danger', label: 'Suspenso' },
  default: { bg: 'bg-bg-3', text: 'text-text-2', label: 'Desconhecido' },
};

// ============================================
// API KEY STATUS
// ============================================

export const apiKeyStatusColors: StatusColorMap = {
  active: { bg: 'bg-success/20', text: 'text-success', label: 'Ativa' },
  expired: { bg: 'bg-warning/20', text: 'text-warning', label: 'Expirada' },
  revoked: { bg: 'bg-error/20', text: 'text-error', label: 'Revogada' },
  inactive: { bg: 'bg-bg-3', text: 'text-text-3', label: 'Inativa' },
  default: { bg: 'bg-bg-3', text: 'text-text-3', label: 'Desconhecido' },
};

// ============================================
// NOTIFICATION TYPE
// ============================================

export const notificationTypeColors: StatusColorMap = {
  info: { bg: 'bg-accent-1/15', text: 'text-accent-1', label: 'Informativo' },
  update: { bg: 'bg-success/15', text: 'text-success', label: 'Atualizacao' },
  warning: { bg: 'bg-warning/15', text: 'text-warning', label: 'Aviso' },
  critical: { bg: 'bg-danger/15', text: 'text-danger', label: 'Critico' },
  default: { bg: 'bg-bg-3', text: 'text-text-2', label: 'Info' },
};

// ============================================
// AUDIT LOG ACTION
// ============================================

export function getAuditActionColor(action: string): string {
  if (action.includes('.create') || action.includes('.grant') || action === 'auth.login_success') {
    return 'text-success bg-success/10';
  }
  if (action.includes('.delete') || action.includes('.revoke') || action === 'auth.login_failed') {
    return 'text-error bg-error/10';
  }
  if (action.includes('.update') || action.includes('password_change')) {
    return 'text-accent-1 bg-accent-1/10';
  }
  return 'text-text-2 bg-bg-3';
}

// ============================================
// MESSAGING TIER
// ============================================

export function formatTier(tier?: string): string {
  if (!tier) return 'N/A';

  const normalized = tier.toUpperCase();
  if (normalized === 'UNLIMITED') return 'Ilimitado';

  const match = normalized.match(/TIER_(.+)/);
  if (match) {
    const value = match[1];
    if (value === '0.25K') return '250/dia';
    if (value.endsWith('K')) return `${value}/dia`;
    return `${value}/dia`;
  }
  return tier;
}
